import { useState } from 'react'
import { lumi } from '@/lib/lumi'
import {X, Mail, Lock, User} from 'lucide-react'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  mode: 'login' | 'register'
}

export default function AuthModal({ isOpen, onClose, mode: initialMode }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'register' | 'reset'>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [userName, setUserName] = useState('')
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  if (!isOpen) return null

  const handleGoogleLogin = () => {
    onClose()
    setTimeout(() => {
      lumi.auth.signInWithOAuth({ provider: 'google' })
    }, 100)
  }

  const handleSendOtp = async () => {
    if (!email) {
      setError('Veuillez entrer votre email')
      return
    }

    setLoading(true)
    setError('')
    try {
      await lumi.auth.sendOtp({ 
        email, 
        type: mode === 'register' ? 'register' : 'resetPassword' 
      })
      setOtpSent(true)
      setCountdown(60)
      
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi du code')
    }
    setLoading(false)
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await lumi.auth.signInWithPassword({ email, password })
      onClose()
      setTimeout(() => {
        window.location.href = '/'
      }, 100)
    } catch (err: any) {
      setError(err.message || 'Email ou mot de passe incorrect')
      setLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!otpSent) {
      setError('Veuillez d\'abord recevoir le code OTP')
      return
    }

    setLoading(true)
    setError('')
    try {
      await lumi.auth.signUpWithPassword({ 
        email, 
        password, 
        otp,
        userName: userName || undefined
      })
      onClose()
      setTimeout(() => {
        window.location.href = '/'
      }, 100)
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'inscription')
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!otpSent) {
      setError('Veuillez d\'abord recevoir le code OTP')
      return
    }

    setLoading(true)
    setError('')
    try {
      await lumi.auth.resetPassword({ 
        email, 
        newPassword: password, 
        otp 
      })
      onClose()
      setTimeout(() => {
        window.location.href = '/'
      }, 100)
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la réinitialisation')
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 relative shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-black mb-2">
            {mode === 'login' ? 'Connexion' : mode === 'register' ? 'Inscription' : 'Mot de passe oublié'}
          </h2>
          <p className="text-gray-600">
            {mode === 'login' 
              ? 'Connectez-vous pour réserver vos véhicules'
              : mode === 'register'
              ? 'Créez votre compte en quelques instants'
              : 'Réinitialisez votre mot de passe'
            }
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={mode === 'login' ? handleLogin : mode === 'register' ? handleRegister : handleResetPassword} className="space-y-4 mb-6">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#fbbf24] focus:outline-none transition-colors"
                placeholder="votre@email.com"
                required
              />
            </div>
          </div>

          {/* Nom (inscription uniquement) */}
          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom (optionnel)</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#fbbf24] focus:outline-none transition-colors"
                  placeholder="Votre nom"
                />
              </div>
            </div>
          )}

          {/* Code OTP (inscription et reset) */}
          {(mode === 'register' || mode === 'reset') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Code de vérification</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#fbbf24] focus:outline-none transition-colors"
                  placeholder="000000"
                  required={mode !== 'login'}
                  disabled={!otpSent}
                />
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={countdown > 0 || loading}
                  className="px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap font-medium"
                >
                  {countdown > 0 ? `${countdown}s` : otpSent ? 'Renvoyer' : 'Envoyer'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Un code sera envoyé à votre adresse email</p>
            </div>
          )}

          {/* Mot de passe */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {mode === 'reset' ? 'Nouveau mot de passe' : 'Mot de passe'}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#fbbf24] focus:outline-none transition-colors"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          {/* Bouton de soumission */}
          <button
            type="submit"
            disabled={loading || ((mode === 'register' || mode === 'reset') && !otpSent)}
            className="w-full py-3 bg-[#fbbf24] text-black font-bold rounded-xl hover:bg-[#f59e0b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Chargement...' : mode === 'login' ? 'Se connecter' : mode === 'register' ? 'Créer mon compte' : 'Réinitialiser'}
          </button>
        </form>

        {/* Séparateur */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Ou continuer avec</span>
          </div>
        </div>

        {/* OAuth Google */}
        <button
          onClick={handleGoogleLogin}
          className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white border-2 border-gray-200 rounded-xl hover:border-[#fbbf24] hover:bg-gray-50 transition-all font-medium text-gray-700 shadow-sm hover:shadow-md"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Google
        </button>

        {/* Liens de navigation */}
        <div className="mt-6 text-center space-y-2">
          {mode === 'login' && (
            <>
              <button
                onClick={() => setMode('reset')}
                className="text-sm text-[#fbbf24] hover:underline"
              >
                Mot de passe oublié ?
              </button>
              <p className="text-sm text-gray-600">
                Pas encore de compte ?{' '}
                <button
                  onClick={() => setMode('register')}
                  className="text-[#fbbf24] hover:underline font-medium"
                >
                  S'inscrire
                </button>
              </p>
            </>
          )}
          {(mode === 'register' || mode === 'reset') && (
            <p className="text-sm text-gray-600">
              Déjà un compte ?{' '}
              <button
                onClick={() => setMode('login')}
                className="text-[#fbbf24] hover:underline font-medium"
              >
                Se connecter
              </button>
            </p>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            En vous connectant, vous acceptez nos{' '}
            <a href="/conditions" className="text-[#fbbf24] hover:underline">
              conditions d'utilisation
            </a>{' '}
            et notre{' '}
            <a href="/confidentialite" className="text-[#fbbf24] hover:underline">
              politique de confidentialité
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
